import { CartItems } from './CartItems'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { InvoiceSummary } from './InvoiceSummary'
import { CouponInput } from './CouponInput'
import { AutoApplyDiscounts } from './AutoApplyDiscounts'
import { useState } from 'react'
import { useAuth } from '../../context/auth-context'
import { Helmet } from 'react-helmet-async'
import { AppliedDiscount } from '../../types/coupon'

interface Product {
  image: string
  title: string
}

interface StatusHistoryType {
  status: string
  changedAt: string | number | Date
}

interface CartItemsTypes {
  discount: number
  id: string
  orderId: string
  ownerId: string
  price: number
  product: Product
  productId: string
  quantity: number
  shipmentTrackingCode: number | null
  status: string
  statusHistory: StatusHistoryType[]
  subTotal: number
  total: number
  vat: number
}

interface CartTypes {
  createdAt: string
  currency: string
  customerId: string
  discount: number
  id: string
  isCancelled: boolean
  isDisputed: boolean
  items: CartItemsTypes[]
  paymentGatewayId: string | null
  receiverFirstName: string
  receiverLastName: string
  receiverTelephone: string
  refundType: string | null
  shipmentTrackingCode: number | null
  shippingAddressCityId: number
  shippingAddressLatitude: number | null
  shippingAddressLongitude: number | null
  shippingAddressPostalCode: string
  shippingAddressText: string
  shippingPrice: number
  shippingRateId: string | null
  status: string
  statusHistory: StatusHistoryType[]
  subTotal: number
  total: number
  totalRefunded: number
  transactionId: string | null
  transactionTrackingCode: string | null
  updatedAt: string
  trackingUrl: string | null
  vat: number
  cartItemIdsBeingRemoved: []
  // Discount-related properties
  appliedDiscounts?: AppliedDiscount[]
  totalDiscountAmount?: number
}

export const Cart = () => {
  const [cart, setCart] = useState<CartTypes>(() => {
    const items = localStorage.getItem('cart')
    const initialValue = items ? JSON.parse(items) : ''
    return initialValue || ''
  })

  const { user } = useAuth()
  const location = useLocation()
  let navigate = useNavigate()

  const handleBackButtonClick = () => {
    navigate('/')
  }

  const handleCouponApplied = (updatedData: any) => {
    // Update cart with new discount information
    const updatedCart = {
      ...cart,
      appliedDiscounts: updatedData.appliedDiscounts,
      totalDiscountAmount: updatedData.totalDiscountAmount,
      subTotal: updatedData.subTotal,
      total: updatedData.total,
    }
    
    setCart(updatedCart)
    localStorage.setItem('cart', JSON.stringify(updatedCart))
    window.dispatchEvent(new Event('cart-changed'))
  }

  const getAppliedCouponCodes = () => {
    if (!cart.appliedDiscounts || cart.appliedDiscounts.length === 0) {
      return []
    }
    return cart.appliedDiscounts
      .filter((d: any) => d.code)
      .map((d: any) => d.code)
  }

  const renderFooter = () => {
    if (!cart.items || cart.items.length === 0) {
      return null
    }

    return (
      <div className='flex flex-col items-center justify-between h-full'>
        <InvoiceSummary cart={cart} />
        <Link
          to={user ? '/cart/shipping-info' : '/login'}
          className='hover:bg-[#81A1C1] p-4 rounded flex items-center justify-center text-white lg:w-full mt-10  transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          state={{ orderId: cart.id, pathname: location.pathname }}
        >
          نهایی‌سازی سفارش
        </Link>
      </div>
    )
  }

  return (
    <div className=' container mx-auto my-10 flex flex-col h-auto'>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          سبد خرید
        </title>
        <meta
          name='description'
          content='داستان کتاب - داستان ها همیشه در دل و ذهن می مانند'
        />
      </Helmet>
      <button
        className='rounded border-none w-full md:w-[24%] p-2 text-white cursor-pointer md:hidden'
        style={{ backgroundColor: 'var(--accent-primary)' }}
        onClick={handleBackButtonClick}
      >
        بازگشت به صفحه اصلی
      </button>
      <div className='w-full my-5 grid grid-cols-1 lg:grid-cols-4 gap-4 h-auto'>
        <div
          className={
            cart?.items?.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4'
          }
        >
          <CartItems cart={cart} setCart={setCart} />
          
          {/* Coupon and discount section - only show if cart has items */}
          {cart?.items?.length > 0 && (
            <div className='mt-4'>
              {/* Auto-apply discounts banner */}
              <AutoApplyDiscounts
                orderSubtotal={cart.subTotal}
                customerId={user?.id}
              />
              
              {/* Coupon input */}
              <CouponInput
                orderId={cart.id}
                orderSubtotal={cart.subTotal}
                customerId={user?.id}
                onCouponApplied={handleCouponApplied}
                appliedCoupons={getAppliedCouponCodes()}
              />
            </div>
          )}
        </div>
        {cart?.items?.length > 0 && (
          <div className='lg:col-span-1 border rounded-md p-4 flex flex-col h-full'>
            {renderFooter()}
          </div>
        )}
      </div>
    </div>
  )
}
